/**
 * Per-variant stock on the product form and Stock Levels. GRN/variants spec
 * sections 2 (decisions 3 to 6) and 5.4–5.5.
 *
 * Everything here READS what the cloud wrote: `variants` is derived by the
 * product write lambdas and `variantStock` by the ledger lambdas. The only
 * config the dashboard writes is `trackByVariant` and `variantConfig`.
 */
import type {ProductVariant, VariantStockEntry} from './Types';

/** The product's variants, as the cloud derived them. */
export const variantsOf = (product: any): ProductVariant[] =>
  Array.isArray(product?.variants) ? product.variants : [];

/**
 * May this product be tracked per variant? Unit mode, and variants the cloud
 * knows about. The second matters for a product whose options were added but
 * not yet saved: until the save, there are no variant ids to count against.
 */
export const canTrackByVariant = (stockMode: string | undefined, product: any) =>
  stockMode === 'unit' && variantsOf(product).length > 0;

/** A variant's par: its own, else the product's. Null for none. */
export function variantPar(
  variantConfig: {[id: string]: {parLevel?: number | null}} | null | undefined,
  productPar: any,
  variantId: string,
): number | null {
  const own = variantConfig?.[variantId]?.parLevel;
  if (own != null && `${own}` !== '' && Number.isFinite(Number(own))) return Number(own);
  return productPar != null && `${productPar}` !== '' && Number.isFinite(Number(productPar))
    ? Number(productPar)
    : null;
}

export type VariantStatus = 'ok' | 'low' | 'sold_out' | 'uncounted';

export interface VariantRow {
  variantId: string;
  label: string;
  onHand: number | null;
  par: number | null;
  status: VariantStatus;
  updatedAt?: string;
}

/**
 * One row per current variant. A variant nobody has counted shows as
 * uncounted, not sold out — the cloud's rule too, so switching tracking on
 * does not paint every variant red.
 */
export function variantRows(product: any): VariantRow[] {
  const stock: {[id: string]: VariantStockEntry} = product?.variantStock ?? {};
  return variantsOf(product).map((v) => {
    const entry = stock[v.variantId];
    const par = variantPar(product?.variantConfig, product?.parLevel, v.variantId);
    if (!entry) {
      return {variantId: v.variantId, label: v.label, onHand: null, par, status: 'uncounted'};
    }
    const onHand = Number(entry.onHand) || 0;
    // eslint-disable-next-line no-nested-ternary
    const status: VariantStatus = entry.soldOut === true || onHand <= 0
      ? 'sold_out'
      : par != null && onHand <= par
        ? 'low'
        : 'ok';
    return {variantId: v.variantId, label: v.label, onHand, par, status, updatedAt: entry.updatedAt};
  });
}

/** "2 of 3 variants sold out", or null when none are. */
export function soldOutSummary(rows: VariantRow[]): string | null {
  const out = rows.filter((r) => r.status === 'sold_out').length;
  if (out === 0) return null;
  return `${out} of ${rows.length} variant${rows.length === 1 ? '' : 's'} sold out`;
}

/**
 * The figure to show right after an adjustment, before the refetch lands:
 * a count replaces, a delivery adds, waste subtracts.
 */
export function afterAdjust(onHand: number | null, mode: 'count' | 'receive' | 'waste', quantity: number): number {
  if (mode === 'count') return quantity;
  const base = onHand ?? 0;
  return mode === 'receive' ? base + quantity : base - quantity;
}

/**
 * variantConfig as it should be saved: blank pars dropped, numbers as
 * numbers, and nothing for variants the product no longer has.
 */
export function cleanVariantConfig(
  config: {[id: string]: {parLevel?: any}} | null | undefined,
  variants: ProductVariant[],
): {[id: string]: {parLevel: number}} | null {
  const out: {[id: string]: {parLevel: number}} = {};
  for (const v of variants) {
    const par = config?.[v.variantId]?.parLevel;
    if (par != null && `${par}`.trim() !== '' && Number.isFinite(Number(par))) {
      out[v.variantId] = {parLevel: Number(par)};
    }
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The quantity the product list shows. The ledger's `stockOnHand` when the
 * product has been counted (for a product tracked per variant it is already
 * the sum across variants); otherwise the legacy hand-typed figures, summed
 * across `priceVariants` when there are any.
 */
export function productQty(product: any): number {
  const onHand = product?.stockOnHand;
  if (onHand != null && `${onHand}` !== '' && Number.isFinite(Number(onHand))) return Number(onHand);
  const priceVariants = Array.isArray(product?.priceVariants) ? product.priceVariants : [];
  if (priceVariants.some((v: any) => v?.stockCount != null && `${v.stockCount}` !== '')) {
    return priceVariants.reduce((sum: number, v: any) => sum + (Number(v?.stockCount) || 0), 0);
  }
  return Number(product?.stockCount) || 0;
}
